import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Snackbar,
  Alert,
  Skeleton,
} from "@mui/material";
import DriverDetailsSection from "./DriverDetailsSection";
import {
  getDobError,
  getLicenseExpiryError,
} from "../../utils/driverDateValidation";
import { DocumentUploadSection } from "./DocumentUploadSection";
import config from "../../config.js";
import {
  buildCreateDriverPayload,
  buildUpdateDriverPayload,
  mapDriverApiToFormData,
  toRequiredInt,
} from "../../utils/driverPayloads";

const inputStyles = {
  height: "auto",
  "& .MuiOutlinedInput-root": {
    height: "55px",
    borderRadius: "16px",
    border: "2px solid #183059",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 24px",
  },
  "& .Mui-error": {
    borderColor: "#d32f2f",
  },
};

const redAsteriskStyle = {
  color: "#d32f2f",
  marginLeft: "2px",
};

const initialFormData = {
  driverId: "",
  organizationId: undefined,
  firstName: "",
  lastName: "",
  email: "",
  phone_number: "",
  driving_license_number: "",
  dob: "",
  current_address: "",
  current_city: "",
  current_pincode: "",
  permanent_address: "",
  permanent_city: "",
  permanent_pincode: "",
  description: "",
  fullName: "",
  sameAsPresentAddress: false,
  isActive: true,
  hasSmartphone: false,
  preferredLanguage: "",
  presentAddress: "",
  presentCity: "",
  presentPincode: "",
  permanentAddress: "",
  permanentCity: "",
  permanentPincode: "",
  phoneNumber: "",
  drivingLicenseId: "",
  dateOfBirth: "",
  licenseExpiryDate: "",
  moreDetails: "",
  vehicle_id: "",
  supervisorId: "",
  _showVehicleSelect: false,
  imagePreview: null,
};

/**
 * DriverInlineForm
 * Renders the same form as DriversAddMain but inline (above the table).
 * Props:
 *   onClose   - called when the user cancels or after successful submission
 *   onSuccess - called after a successful create so the parent can refresh data
 *   editId    - optional driver id when editing (pass null for create mode)
 *   editData  - optional pre-filled row data when editing
 */
export default function DriverInlineForm({ onClose, onSuccess, editId = null, editData = null }) {
  const [organizationId, setOrganizationId] = useState();
  const [formData, setFormData] = useState({ ...initialFormData });
  const [documents, setDocuments] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isUploaded, setIsUploaded] = useState(false);
  const [preview, setPreview] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [numericDriverId, setNumericDriverId] = useState(editId);
  const [isFormLoading, setIsFormLoading] = useState(!!editId);
  const [allVehicles, setAllVehicles] = useState([]);
  const [supervisors, setSupervisors] = useState([]);

  const clearDobErrors = (nextErrors) => {
    delete nextErrors.dateOfBirth;
    delete nextErrors.dob;
  };

  const setDobError = (nextErrors, message) => {
    nextErrors.dateOfBirth = message;
    nextErrors.dob = message;
  };

  // Load organizationId from localStorage
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("userState"));
    const storedOrgId = storedData?.user?.organizationId;
    if (storedOrgId) {
      setOrganizationId(parseInt(storedOrgId, 10));
    }
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, organizationId }));
  }, [organizationId]);

  // Fetch vehicles
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await axios.get(`${config.baseURL}/vehicle/organization`, { withCredentials: true });
        setAllVehicles(Array.isArray(res.data) ? res.data : []);
      } catch (e) { console.error("Error fetching vehicles", e); }
    };
    fetchVehicles();
  }, []);

  // Fetch supervisors (org users)
  useEffect(() => {
    const fetchSupervisors = async () => {
      if (!organizationId) return;
      try {
        const res = await axios.get(`${config.baseURL}/user/organization/${organizationId}`, { withCredentials: true });
        const users = res.data?.users || res.data || [];
        setSupervisors(Array.isArray(users) ? users : []);
      } catch (e) { console.error("Error fetching supervisors", e); }
    };
    fetchSupervisors();
  }, [organizationId]);

  // Fetch driver data if editing
  const fetchDriverById = async (id) => {
    try {
      const response = await axios.get(`${config.baseURL}/driver/id`, {
        params: { id: toRequiredInt(id) },
        withCredentials: true,
      });
      const driverData = response.data;
      setNumericDriverId(driverData.id);

      const driverImageDoc = driverData.documents?.find(
        (doc) => doc.name === "DriverImage"
      );
      const profilePictureUrl = driverImageDoc
        ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(driverImageDoc.location)}`
        : null;

      setFormData({
        ...mapDriverApiToFormData(driverData, organizationId),
        imagePreview: profilePictureUrl,
      });

      setDocuments(driverData.documents || []);

      if (profilePictureUrl) {
        try {
          await axios.head(profilePictureUrl, { withCredentials: true });
          setSelectedImage({ location: profilePictureUrl, isExisting: true, fileId: driverImageDoc?.id });
          setPreview(profilePictureUrl);
          setIsUploaded(true);
        } catch (e) {
          setSelectedImage({ location: profilePictureUrl, isExisting: true, fileId: driverImageDoc?.id, inaccessible: true });
          setPreview(profilePictureUrl);
          setIsUploaded(true);
          setImageError("Profile image is not available");
        }
      } else {
        setSelectedImage(null);
        setPreview(null);
        setIsUploaded(false);
      }

      return driverData;
    } catch (error) {
      console.error("Error fetching driver:", error);
      setSubmitError("Failed to fetch driver data");
      setShowAlert(true);
      return null;
    }
  };

  useEffect(() => {
    const getDriverData = async () => {
      if (editId) {
        setIsFormLoading(true);
        setIsSubmitting(true);
        await fetchDriverById(editId);
        setIsSubmitting(false);
        setIsFormLoading(false);
      }
    };
    getDriverData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, organizationId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => {
      const newState = { ...prevState, [name]: type === "checkbox" ? checked : value };

      if (name === "firstName" || name === "lastName") {
        newState.fullName = `${name === "firstName" ? value : prevState.firstName} ${name === "lastName" ? value : prevState.lastName}`.trim();
      }

      if (name === "sameAsPresentAddress" && checked) {
        newState.permanentAddress = prevState.presentAddress;
        newState.permanentCity = prevState.presentCity;
        newState.permanentPincode = prevState.presentPincode;
        newState.permanent_address = prevState.current_address;
        newState.permanent_city = prevState.current_city;
        newState.permanent_pincode = prevState.current_pincode;
      }

      // Bi-directional mapping
      if (name === "presentCity") newState.current_city = value;
      else if (name === "presentAddress") newState.current_address = value;
      else if (name === "presentPincode") newState.current_pincode = value;
      else if (name === "permanentCity") newState.permanent_city = value;
      else if (name === "permanentAddress") newState.permanent_address = value;
      else if (name === "permanentPincode") newState.permanent_pincode = value;
      else if (name === "dateOfBirth") newState.dob = value;
      else if (name === "phoneNumber") newState.phone_number = value;
      else if (name === "drivingLicenseId") newState.driving_license_number = value;
      else if (name === "moreDetails") newState.description = value;

      if (name === "current_city") newState.presentCity = value;
      else if (name === "current_address") newState.presentAddress = value;
      else if (name === "current_pincode") newState.presentPincode = value;
      else if (name === "permanent_city") newState.permanentCity = value;
      else if (name === "permanent_address") newState.permanentAddress = value;
      else if (name === "permanent_pincode") newState.permanentPincode = value;
      else if (name === "dob") newState.dateOfBirth = value;
      else if (name === "phone_number") newState.phoneNumber = value;
      else if (name === "driving_license_number") newState.drivingLicenseId = value;
      else if (name === "description") newState.moreDetails = value;

      return newState;
    });

    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, type === "checkbox" ? checked : value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "firstName":
        if (!value.trim()) newErrors[name] = "First Name is required";
        else delete newErrors[name];
        break;
      case "lastName":
        if (!value.trim()) newErrors[name] = "Last Name is required";
        else delete newErrors[name];
        break;
      case "driverId":
        if (!value.trim()) newErrors[name] = "Driver ID is required";
        else delete newErrors[name];
        break;
      case "drivingLicenseId":
        if (!value.trim()) newErrors[name] = "Driving License ID is required";
        else delete newErrors[name];
        break;
      case "presentCity":
        if (!value.trim()) newErrors[name] = "Present City is required";
        else delete newErrors[name];
        break;
      case "permanentCity":
        if (!formData.sameAsPresentAddress && !value.trim()) newErrors[name] = "Permanent City is required";
        else delete newErrors[name];
        break;
      case "sameAsPresentAddress":
        if (value) delete newErrors.permanentCity;
        break;
      case "dateOfBirth":
      case "dob": {
        const dobError = getDobError(value);
        if (dobError) setDobError(newErrors, dobError);
        else clearDobErrors(newErrors);
        break;
      }
      case "licenseExpiryDate": {
        const expiryError = getLicenseExpiryError(value);
        if (expiryError) newErrors.licenseExpiryDate = expiryError;
        else delete newErrors.licenseExpiryDate;
        break;
      }
      default:
        delete newErrors[name];
        break;
    }
    setErrors(newErrors);
  };

  const validateForm = () => {
    let isValid = true;
    let newTouched = { ...touched };
    let newErrors = { ...errors };

    newTouched["firstName"] = true;
    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors["firstName"] = "First Name is required";
      isValid = false;
    } else {
      delete newErrors["firstName"];
    }

    newTouched["lastName"] = true;
    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors["lastName"] = "Last Name is required";
      isValid = false;
    } else {
      delete newErrors["lastName"];
    }

    const otherRequiredFields = ["driverId", "drivingLicenseId", "presentCity"];
    if (!formData.sameAsPresentAddress) otherRequiredFields.push("permanentCity");

    for (const field of otherRequiredFields) {
      newTouched[field] = true;
      const value = formData[field];
      if (!value || (typeof value === "string" && !value.trim())) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, " $1")} is required`;
        isValid = false;
      } else {
        delete newErrors[field];
      }
    }

    newTouched.dateOfBirth = true;
    newTouched.dob = true;
    const dobError = getDobError(formData.dateOfBirth || formData.dob);
    if (dobError) {
      setDobError(newErrors, dobError);
      isValid = false;
    } else {
      clearDobErrors(newErrors);
    }

    newTouched.licenseExpiryDate = true;
    const expiryError = getLicenseExpiryError(formData.licenseExpiryDate);
    if (expiryError) {
      newErrors.licenseExpiryDate = expiryError;
      isValid = false;
    } else {
      delete newErrors.licenseExpiryDate;
    }

    setTouched(newTouched);
    setErrors(newErrors);
    return isValid;
  };

  const uploadProfilePicture = async (driverId, file) => {
    const formDataUpload = new FormData();
    formDataUpload.append("image", file);
    const response = await axios.post(
      `${config.baseURL}/upload/image?name=${encodeURIComponent("DriverImage")}&associateEntityId=${driverId}&associatedWith=driver&type=profile`,
      formDataUpload,
      { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true }
    );
    return response.data;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    if (validateForm()) {
      try {
        let dataToSend;
        let currentDriverId;
        const editIdNumber = toRequiredInt(editId);

        if (editId) {
          if (editIdNumber === null) {
            throw new Error("Invalid driver id");
          }

          dataToSend = buildUpdateDriverPayload(formData);
          const response = await axios.put(`${config.baseURL}/driver/${editIdNumber}`, dataToSend, { withCredentials: true });
          console.log("Driver updated successfully:", response.data);
          currentDriverId = editIdNumber;
        } else {
          dataToSend = buildCreateDriverPayload(formData);
          const response = await axios.post(`${config.baseURL}/driver`, dataToSend, { withCredentials: true });
          console.log("Driver created successfully:", response);
          currentDriverId = response.data.id;
          setNumericDriverId(response.data.id);
        }

        if (selectedImage instanceof File) {
          try {
            const response = await uploadProfilePicture(currentDriverId, selectedImage);
            const newImageUrl = `${config.baseURL}/upload/image?fileName=${encodeURIComponent(response.data.location)}`;
            setIsUploaded(true);
            setPreview(newImageUrl);
            setSelectedImage({ location: newImageUrl, isExisting: true });
          } catch (imageError) {
            console.error("Profile picture upload failed:", imageError);
          }
        }

        setSubmitSuccess(true);
        setShowAlert(true);
        return currentDriverId;
      } catch (error) {
        console.error("Submission error:", error.response?.data?.error || error.message);
        setSubmitError(error.response?.data?.error || error.message || "Failed to submit driver data");
        setShowAlert(true);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }

    setIsSubmitting(false);
    return null;
  };

  const handleComplete = () => {
    setSubmitSuccess(true);
    setShowAlert(true);
    setTimeout(() => {
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    }, 1000);
  };

  const hasError = (field) => touched[field] && Boolean(errors[field]);
  const getErrorMessage = (field) => (touched[field] ? errors[field] : "");

  const handleAlertClose = () => {
    setShowAlert(false);
    if (submitSuccess) {
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    }
  };

  if (isFormLoading) {
    return (
      <Box sx={{ margin: "0px 0px 20px 0px" }}>
        <Paper sx={{ borderRadius: "8px", overflow: "hidden", padding: "20px", boxShadow: "-2px 2px 5px 0px #00000040" }}>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={40} width={120} sx={{ mt: 2 }} />
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ margin: "0px 0px 20px 0px" }}>
        <Box
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
         
          }}
        >
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <DriverDetailsSection
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              imageError={imageError}
              setImageError={setImageError}
              hasError={hasError}
              getErrorMessage={getErrorMessage}
              inputStyles={inputStyles}
              redAsteriskStyle={redAsteriskStyle}
              isUploaded={isUploaded}
              preview={preview}
              allVehicles={allVehicles}
              supervisors={supervisors}
              onClose={onClose}
              isEditing={!!editId}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
        
            marginTop: 3,
          }}
        >
          <DocumentUploadSection
            driverId={numericDriverId}
            documents={documents}
            setDocuments={setDocuments}
            isEditing={!!editId}
            orgId={organizationId}
            onSubmit={handleSubmit}
            onCancel={onClose}
            validateDetailsForm={validateForm}
            onComplete={handleComplete}
          />
        </Box>
        <Box sx={{ height: 9 }} />
      </Box>

      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleAlertClose} severity={submitSuccess ? "success" : "error"} sx={{ width: "100%" }}>
          {submitSuccess
            ? `Driver data successfully ${editId ? "updated" : "created"}!`
            : submitError || "An error occurred while submitting the form."}
        </Alert>
      </Snackbar>
    </>
  );
}
